<template>
  <div class="page-container">
    <div class="form-container">
      <van-form @submit="handleSubmit">
        <!-- 基本信息卡片 -->
        <div class="form-card">
          <div class="card-title">基本信息</div>
          <van-cell-group :border="false">
            <van-field
              v-model="state.formData.communicationType"
              label="沟通类型"
              placeholder="请选择沟通类型"
              readonly
              is-link
              required
              @click="showTypePicker = true"
              :rules="[{ required: true, message: '请选择沟通类型' }]"
            />

            <van-field
              v-model="state.formData.communicationDate"
              label="沟通日期"
              placeholder="请选择沟通日期"
              readonly
              is-link
              required
              @click="handleShowDatePicker"
              :rules="[{ required: true, message: '请选择沟通日期' }]"
            />
            <van-field
              v-model="state.formData.communicationTime"
              label="沟通时间"
              placeholder="请选择沟通时间"
              readonly
              is-link
              required
              @click="handleShowTimePicker"
              :rules="[{ required: true, message: '请选择沟通时间' }]"
            />
          </van-cell-group>
        </div>

        <!-- 沟通内容卡片 -->
        <div class="form-card">
          <div class="card-title">沟通内容
            <span class="required-mark">*</span>
          </div>
          
          <div class="content-field-wrapper">
            <van-field
              v-model="state.formData.communicationContent"
              label=""
              type="textarea"
              rows="5"
              placeholder="请详细描述本次沟通的具体内容..."
              required
              :rules="[{ required: true, message: '请输入沟通内容' }]"
            />
          </div>
        </div>

        <!-- 图片上传卡片 -->
        <div class="form-card">
          <div class="card-title">
            沟通图片
            <span class="required-mark">*</span>
            <span class="upload-tip">最多上传9张</span>
          </div>
          <div class="upload-section">
            <van-uploader
              v-model="state.fileList"
              :after-read="afterRead"
              :before-delete="beforeDelete"
              :max-count="9"
              accept="image/*"
            >
              <template #preview-cover="{ file }">
                <div class="preview-cover">
                  <van-icon name="cross" @click.stop="handleDeleteImage(file)" />
                </div>
              </template>
            </van-uploader>
          </div>
        </div>

        <!-- 提交按钮 -->
        <div class="submit-button">
          <van-button
            round
            type="primary"
            block
            native-type="submit"
            :loading="state.loading"
          >
            保存
          </van-button>
        </div>
      </van-form>
    </div>

    <!-- 沟通类型选择器 -->
    <van-popup v-model:show="showTypePicker" position="bottom" :style="{ paddingBottom: 'var(--van-safe-area-inset-bottom)' }">
      <van-picker
        :columns="state.typeColumns"
        @confirm="onTypeConfirm"
        @cancel="showTypePicker = false"
      />
    </van-popup>

    <!-- 日期选择器 -->
    <van-popup v-model:show="showDatePicker" position="bottom" :style="{ paddingBottom: 'var(--van-safe-area-inset-bottom)' }">
      <van-date-picker
        v-model="state.currentDate"
        @confirm="onDateConfirm"
        @cancel="showDatePicker = false"
        :columns-type="columnsType"
        :min-date="minDate"
      />
    </van-popup>

    <!-- 时间选择器 -->
    <van-popup v-model:show="showTimePicker" position="bottom" :style="{ paddingBottom: 'var(--van-safe-area-inset-bottom)' }">
      <van-time-picker
        v-model="state.currentTime"
        @confirm="onTimeConfirm"
        :columns-type="columnsTime"
        @cancel="showTimePicker = false"
      />
    </van-popup>
  </div>
</template>

<script>
import { defineComponent, onMounted, reactive, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { showNotify } from 'vant';
import { uploadFile } from '@/api/uploadFile';
import { queryAddCommunication } from '@/api/user';
import dayjs from 'dayjs';
import { postSyparamSelectList } from "@/api/report";


export default defineComponent({
  name: 'AddCommunication',

  setup() {
    const route = useRoute();
    const router = useRouter();

    const state = reactive({
      formData: {
        communicationType: '',
        communicationTypeValue: '',
        communicationDate: '',
        communicationTime: '',
        communicationContent: '',
      },
      fileList: [],
      uploadedImages: [], // 存储已上传的图片URL
      typeColumns: [],
      currentDate: null, // 日期选择器当前值，数组格式 [year, month, day]
      currentTime: null, // 时间选择器当前值，数组格式 [hour, minute]
      loading: false
    });

    const columnsType = ['year', 'month', 'day'];
    const columnsTime = ['hour', 'minute'];

    // 设置最小日期为一年前，允许选择今天之前的日期
    const minDate = new Date(new Date().setFullYear(new Date().getFullYear() - 1));

    // 弹框控制变量使用 ref
    const showTypePicker = ref(false);
    const showDatePicker = ref(false);
    const showTimePicker = ref(false);

    onMounted(() => {
      // 设置默认值为当前日期和时间
      const now = dayjs();
      state.formData.communicationDate = now.format('YYYY-MM-DD');
      state.formData.communicationTime = now.format('HH:mm');
      state.currentDate = [now.year(), now.month() + 1, now.date()];
      state.currentTime = [now.hour(), now.minute()];
      
      // 加载沟通类型选项
      loadCommunicationTypes();
    });

    // 加载沟通类型枚举
    const loadCommunicationTypes = async () => {
      try {
        const result = await postSyparamSelectList({ bigCode: "contact_type" });
        if (result.data) {
            state.typeColumns = result.data.map(item => {
            return {
                text: item.descripition,
                value: item.value
            }
            });
        }
      } catch (error) {
        console.error('加载沟通类型失败', error);
      }
    };

    // 处理图片上传
    const afterRead = async (file) => {
      state.loading = true;
      try {
        const { data: result } = await uploadFile(file, {
          bsnType: 7 // 使用与用户头像上传相同的业务类型
        });

        if (result.code === '00' && result.data?.[0]?.fileUrl) {
          // 保存上传成功的图片URL
          state.uploadedImages.push(result.data[0].fileUrl);
          showNotify({
            type: 'success',
            message: '图片上传成功',
            duration: 1000
          });
        } else {
          showNotify({
            type: 'danger',
            message: '图片上传失败',
            duration: 2000
          });
          // 移除上传失败的图片
          const index = state.fileList.findIndex(item => item === file);
          if (index !== -1) {
            state.fileList.splice(index, 1);
          }
        }
      } catch (error) {
        console.error('上传失败', error);
        showNotify({
          type: 'danger',
          message: '图片上传失败',
          duration: 2000
        });
        const index = state.fileList.findIndex(item => item === file);
        if (index !== -1) {
          state.fileList.splice(index, 1);
        }
      } finally {
        state.loading = false;
      }
    };

    // 删除图片
    const beforeDelete = (file, detail) => {
      const index = state.fileList.findIndex(item => item === file);
      if (index !== -1 && state.uploadedImages[index]) {
        // 移除对应的上传图片URL
        state.uploadedImages.splice(index, 1);
      }
      return true;
    };

    // 手动删除图片
    const handleDeleteImage = (file) => {
      const index = state.fileList.findIndex(item => item === file);
      if (index !== -1) {
        state.fileList.splice(index, 1);
        if (state.uploadedImages[index]) {
          state.uploadedImages.splice(index, 1);
        }
      }
    };

    // 选择沟通类型
    const onTypeConfirm = ({ selectedOptions }) => {
      state.formData.communicationType = selectedOptions[0].text;
      state.formData.communicationTypeValue = selectedOptions[0].value;
      showTypePicker.value = false;
    };

    // 显示日期选择器
    const handleShowDatePicker = () => {
      // 如果已有选中的日期，将其设置到选择器中；否则使用当前时间
      if (state.formData.communicationDate) {
        const date = dayjs(state.formData.communicationDate);
        state.currentDate = [date.year(), date.month() + 1, date.date()];
      } else {
        const now = dayjs();
        state.currentDate = [now.year(), now.month() + 1, now.date()];
      }
      showDatePicker.value = true;
    };

    // 显示时间选择器
    const handleShowTimePicker = () => {
      // 如果已有选中的时间，将其设置到选择器中；否则使用当前时间
      if (state.formData.communicationTime) {
        const [hour, minute] = state.formData.communicationTime.split(':');
        state.currentTime = [parseInt(hour), parseInt(minute)];
      } else {
        const now = dayjs();
        state.currentTime = [now.hour(), now.minute()];
      }
      showTimePicker.value = true;
    };

    // 选择日期
    const onDateConfirm = ({ selectedValues }) => {
      const [year, month, day] = selectedValues;
      state.formData.communicationDate = dayjs().year(year).month(month - 1).date(day).format('YYYY-MM-DD');
      showDatePicker.value = false;
    };

    // 选择时间
    const onTimeConfirm = ({ selectedValues }) => {
      const [hour, minute] = selectedValues;
      state.formData.communicationTime = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
      showTimePicker.value = false;
    };

    // 提交表单
    const handleSubmit = async () => {
      // 验证必填字段
      if (!state.formData.communicationType) {
        showNotify({
          type: 'warning',
          message: '请选择沟通类型',
          duration: 2000
        });
        return;
      }

      if (!state.formData.communicationDate) {
        showNotify({
          type: 'warning',
          message: '请选择沟通日期',
          duration: 2000
        });
        return;
      }

      if (!state.formData.communicationTime) {
        showNotify({
          type: 'warning',
          message: '请选择沟通时间',
          duration: 2000
        });
        return;
      }

      if (!state.formData.communicationContent) {
        showNotify({
          type: 'warning',
          message: '请输入沟通内容',
          duration: 2000
        });
        return;
      }

      state.loading = true;

      try {
        // 组合日期和时间
        const fullDateTime = `${state.formData.communicationDate} ${state.formData.communicationTime}:00`;
        
        const result = await queryAddCommunication({
          stuId: route.query.userId,
          type: state.formData.communicationTypeValue,
          contactTime: fullDateTime,
          content: state.formData.communicationContent,
          images: state.uploadedImages.join(',') // 将图片URL数组转换为逗号分隔的字符串
        });

        if (result.code === '00') {
          showNotify({
            type: 'success',
            message: '添加沟通记录成功',
            duration: 2000
          });
          
          // 返回上一页
          setTimeout(() => {
            router.back();
          }, 500);
        } else {
          showNotify({
            type: 'warning',
            message: result.message || '添加沟通记录失败',
            duration: 2000
          });
        }
      } catch (error) {
        console.error('提交失败', error);
        showNotify({
          type: 'danger',
          message: '添加沟通记录失败',
          duration: 2000
        });
      } finally {
        state.loading = false;
      }
    };

    // 返回上一页
    const handleGoBack = () => {
      router.back();
    };

    return {
      state,
      showTypePicker,
      showDatePicker,
      showTimePicker,
      minDate,
      afterRead,
      beforeDelete,
      handleDeleteImage,
      onTypeConfirm,
      handleShowDatePicker,
      handleShowTimePicker,
      onDateConfirm,
      onTimeConfirm,
      handleSubmit,
      handleGoBack,
      columnsType,
      columnsTime
    };
  }
});
</script>

<style lang="scss" scoped>
.page-container {
  background: #f7f8fa;
  min-height: 100vh;
}

.form-container {
  padding: 12px;
}

// 表单卡片样式
.form-card {
  background: #fff;
  border-radius: 12px;
  margin-bottom: 16px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);

  .card-title {
    font-size: 15px;
    font-weight: 600;
    color: #323233;
    padding: 16px;
    border-bottom: 1px solid #ebedf0;
    display: flex;
    align-items: center;
    gap: 6px;
    padding-left: 12px;

    .required-mark {
      color: #ee0a24;
      font-size: 14px;
    }

    .upload-tip {
      font-size: 12px;
      color: #969799;
      font-weight: 400;
      margin-left: auto;
    }
  }
}

// 内容区域包装器
.content-field-wrapper {
  padding: 16px;
  padding-top: 8px;

  :deep(.van-field) {
    padding: 0;
    background: transparent;
  }

  :deep(.van-field__body) {
    textarea {
      font-size: 14px;
      line-height: 1.6;
      color: #323233;
    }
  }
}

// 上传区域
.upload-section {
  padding-top: 12px;
  display: flex;
  margin-left: 12px;

  :deep(.van-uploader) {
    .van-uploader__preview-image {
      border-radius: 8px;
      overflow: hidden;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      width: 100px;
      height: 100px;
    }

    .van-uploader__upload {
      border-radius: 8px;
      background: #f7f8fa;
      border: 1px dashed #dcdee0;
      transition: all 0.3s ease;
      width: 100px;
      height: 100px;

      &:active {
        background: #ebedf0;
      }

      .van-uploader__upload-icon {
        font-size: 24px;
        color: #969799;
      }
    }
  }
}

// 预览遮罩
.preview-cover {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 8px;
  opacity: 0;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }

  .van-icon {
    color: #fff;
    font-size: 20px;
  }
}

// 提交按钮
.submit-button {
  padding: 32px 16px 24px;

  .van-button {
    height: 48px;
    font-size: 16px;
    font-weight: 600;
    box-shadow: 0 4px 12px rgba(25, 137, 250, 0.3);
    transition: all 0.3s ease;

    &:active {
      transform: translateY(1px);
      box-shadow: 0 2px 8px rgba(25, 137, 250, 0.2);
    }
  }
}

// 单元格组样式
:deep(.van-cell-group) {
  border-radius: 0;
  overflow: hidden;
  background: transparent;

  .van-cell {
    padding: 12px 0;
    
    &:not(:last-child)::after {
      border-bottom-color: #f0f0f0;
    }
  }
}

// 字段标签样式
:deep(.van-field__label) {
  min-width: 80px;
  font-size: 14px;
  color: #646566;
  font-weight: 500;
  padding-left: 6px;
}

// 字段输入框样式
:deep(.van-field__value) {
  font-size: 14px;
  
  &::placeholder {
    color: #c0c4cc;
  }
}

// 必填标记样式
:deep(.van-field__required-mark) {
  color: #ee0a24;
  font-size: 14px;
}

// 只读字段样式
:deep(.van-field__control[readonly]) {
  color: #323233;
}

// 文本域样式优化
:deep(.van-field__control--textarea) {
  min-height: 120px;
  padding: 12px 0;
}

// 清除上传图片时悬停显示删除按钮
:deep(.van-uploader__preview) {
  &:hover .preview-cover {
    opacity: 1;
  }
}

// 响应式优化
@media (max-width: 375px) {
  .form-container {
    padding: 12px;
  }

  .form-card {
    margin-bottom: 12px;
    border-radius: 10px;

    .card-title {
      padding: 14px;
      font-size: 14px;
    }
  }
}
</style>

